import React, { useEffect, useState, useMemo } from 'react';
import { Button, Col, Modal, Row, Form, DatePicker, Typography, Badge, Checkbox, Select } from 'antd';
import { PlusSquareOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import moment from 'moment';

import CustomTable from 'src/components/common/CustomTable';
import { GET_COMPANY_CONTENTS } from 'src/operations/queries/company';
import { ADD_BOOK_TO_CLASS, REMOVE_SINGLE_LESSON } from 'src/operations/mutations/classRoom';
import { CLASS_LESSON_LIST } from 'src/operations/queries/getClass';
import {
  UPDATE_CLASS_LESSON,
  UPDATE_CLASS_LESSON_FOR_HOMEWORK_STYLE,
  UPDATE_CLASS_LESSON_TABLE,
} from 'src/operations/mutations/updateClassLesson';
import { userInfoVar } from 'src/apollo/cache';
import { openNotification } from '../common/Notification';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

import * as mutations from 'src/operations/mutations';
import * as queries from 'src/operations/queries';

const { Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const sortSchedule = (a, b) => {
  if (a.unit === b.unit) {
    if (a.lesson === b.lesson) {
      return 0;
    }
    if (a.lesson > b.lesson) {
      return 1;
    }
    return -1;
  }
  if (a.unit > b.unit) {
    return 1;
  }
  return -1;
};

function ClassScheduleModal({ visible, handleCancel, class_idx, classItems, refetchClass }) {
  const isMiddle = userInfoVar()?.campus.company?.name === 'evinemiddle';
  const [form] = Form.useForm();
  const [studyList, setStudyList] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectDate, setSelectDate] = useState(null);
  const [classLessonIdx, setClassLessonIdx] = useState(null);
  const [bookCodes, setBookCodes] = useState([]);
  const [reset, setReset] = useState(0);
  const [classBooks, setClassBooks] = useState(classItems?.class_book || []);
  const [deleteActive, setDeleteActive] = useState(false);
  const iconColor = useMemo(
    () => ({
      v: '#ffce54',
      s: '#5d9cec',
      e: '#a0d468',
      r: '#ff0000',
      voca: '#ffce54',
      sentence: '#5d9cec',
      essay: '#a0d468',
      recording: '#ea4444',
      t: '#4fc1e9',
      rc: '#f044b7',
      lc1: '#3fde13',
      lc2: '#3fde13',
      d: '#2b6349',
      w: '#ac92ec',
      g: '#7c54ff',
      translation :'#4fc1e9',
      dictation1: '#2b6349',
      dictation2: '#2b6349',
      grammar: '#7c54ff',
    }),
    [],
  );

  const classColor = useMemo(
    () => ({
      ClassLesson: '#11783D',
      UserLesson: '#11783D',
      AssignedTests: '#ed5565',
      ZoomSchedule: '#3C79F6',
    }),
    [],
  );

  const classType = useMemo(
    () => ({
      ClassLesson: 'H',
      UserLesson: 'H',
      AssignedTests: 'WB',
      ZoomSchedule: 'Z',
    }),
    [],
  );

  const isAutoSchedule = useMemo(() => {
    const company = userInfoVar()?.campus?.company;
    if (company?.is_auto_schedule === '0') {
      return false;
    }
    return true;
  }, []);

  const {
    data,
    loading: studentLoading,
    refetch,
  } = useQuery(queries.getClass.CLASS_LESSON_LIST, {
    fetchPolicy: 'no-cache',
    skip: !class_idx,
    variables: { class_idx },
  });

  const [updateClassLession] = useMutation(UPDATE_CLASS_LESSON, {
    variables: {
      class_idx,
      class_lesson_idx: classLessonIdx,
      formData: studyList.filter((item) => item.key === classLessonIdx),
      type: '',
    },
    onCompleted: async (data) => {
      if (data) {
        await refetch();
        openNotification('일정 수정 완료!');
      }
    },
  });

  const [update, { loading: loadingUpdateClassLesson }] = useMutation(
    isAutoSchedule ? UPDATE_CLASS_LESSON_TABLE : UPDATE_CLASS_LESSON_FOR_HOMEWORK_STYLE,
    {
      fetchPolicy: 'no-cache',
      variables: { dateList: studyList, class_idx },
      refetchQueries: [
        {
          query: CLASS_LESSON_LIST,
          skip: !class_idx,
          variables: { class_idx },
        },
      ],
      onCompleted: async (data) => {
        if (data) {
          await refetch();
          openNotification('일정 수정 완료!');
        }
      },
      onError(error) {
        console.log('error', error);
      },
    },
  );
  const { data: dataBookContents } = useQuery(GET_COMPANY_CONTENTS, {
    skip: !userInfoVar(),
    variables: { company_idx: userInfoVar()?.campus.company.idx },
    fetchPolicy: 'no-cache',
  });

  const bookList = useMemo(() => {
    if (dataBookContents?.getCompanyBookContentList) {
      let bookList = [];
      dataBookContents?.getCompanyBookContentList.forEach((bookContent) => {
        if (bookContent?.book_list) {
          bookContent.book_list.forEach((book) => {
            bookList.push({
              key: book.code,
              value: book.volume_name || book.code,
            });
          });
        }
      });

      return bookList;
    }
    return [];
  }, [dataBookContents]);
  const [addBookToCLass, { loading: addBookLoading }] = useMutation(ADD_BOOK_TO_CLASS, {
    refetchQueries: [
      {
        query: CLASS_LESSON_LIST,
        skip: !class_idx,
        variables: { class_idx },
      },
    ],
    onCompleted: (data) => {
      if (data) {
        refetch();
        setClassBooks(data.addBookToClass.class_book);

        openNotification('일정 수정 완료!');
      }
    },
  });
  const handelSaveAddBooks = () => {
    Modal.confirm({
      icon: null,
      title: <p style={{ textAlign: 'center' }}>입력하신 정보로 수정하시겠습니까?</p>,
      cancelText: '아니오',
      okText: '네',
      content: (
        <p style={{ color: 'red', textAlign: 'center' }}>
          <span>※주의 사항 </span>
          <br /> "네"를 누른 후에 일정을 변경하려면 수동으로 해야하니 잘 확인하고 선택하시길 바랍니다.
        </p>
      ),
      onOk: async () => {
        const dayOfWeekData = '0000000';
        await addBookToCLass({
          variables: {
            class_idx,
            book_codes: bookCodes,
            begin_date: !selectDate ? moment().format('YYYY-MM-DD') : moment(selectDate).format('YYYY-MM-DD'),
            day_of_week: dayOfWeekData,
            is_auto_schedule: isAutoSchedule,
            each_day_count: [],
          },
        });
        setBookCodes([]);
        setSelectDate(null);
        refetchClass();
      },
    });
  };
  const handelDeleteBooks = () => {
    if (deleteActive) {
      setDeleteActive(false);
      return;
    }

    Modal.confirm({
      icon: null,
      title: <p style={{ textAlign: 'center' }}>등록한 교재를 삭제하시겠습니까?</p>,
      cancelText: '아니오',
      okText: '네',
      content: (
        <>
          <p style={{ textAlign: 'center' }}>
            맞으면 "네"를 누른 후에 <span style={{ color: 'black', fontWeight: '700' }}>삭제할 레슨을 모두 삭제하길 바랍니다.</span>
          </p>
          <p style={{ color: 'red', textAlign: 'center' }}>
            <span>※주의 사항 </span>
            <br /> 삭제한 레슨을 학생이 이미 학습한 경우에도 학습기록은 사라집니다. 잘 확인하고 삭제하시길 바랍니다. 만약, 삭제를 원하지
            않으면 "아니오"를 누르세요.
          </p>
        </>
      ),
      onOk: () => {
        setDeleteActive(!deleteActive);
      },
    });
  };
  console.log(events,'events');
  useEffect(() => {
    if (data?.classLessonList) {
      let nthEvent = 0;
      const tmpEvent = data.classLessonList.map((item, i) => {
        const color = item?.book_lesson?.stage_group_code
          ? iconColor[item?.book_lesson?.stage_group_code]
          : classColor[`${item.__typename}`];
          console.log(item?.book_lesson?.stage_group_code,'item?.book_lesson?.stage_group_code')
        return {
          key: item.idx,
          date: item.study_date,
          title: item.book_lesson.title,
          color,
          classLessonIdx: item.idx,
          unit: item?.book_lesson?.unit_no,
          lesson: item?.book_lesson?.day_no,
          lesson_code: item.lesson_code,
        };
      });
      setEvents(isMiddle ? tmpEvent : tmpEvent.sort(sortSchedule));
      //정렬 문제 때문에 sort 추가함.
      //
      let nth = 0;
      const tmpStudyList = data.classLessonList.reduce((acc, cur, index) => {
        const obj = {
          key: cur.idx,
          number: `${index + 1}회차`,
          lesson_code: cur.lesson_code,
          title: cur.book_lesson.title,
          unit: cur.book_lesson.unit_no,
          lesson: cur.book_lesson.day_no,
          studyDatePrev: cur.study_date,
          studyDate: cur.study_date,
          group:
            iconColor[cur.book_lesson?.stage_group_code] && cur.book_lesson?.stage_group_code.length < 4
              ? cur.book_lesson?.stage_group_code
              : cur.book_lesson?.stage_group_code?.split('')[0],
        };

        return [...acc, obj];
      }, []);
      setStudyList(
        isMiddle
          ? tmpStudyList
          : tmpStudyList.sort(sortSchedule).map((item, key, orgArr) => {
              let copy = '';
              if (key !== 0 && item.lesson_code === orgArr[key - 1].lesson_code) {
                copy = ' (복사)';
              } else {
                nth++;
              }
              return { ...item, number: `${nth}회차${copy}` };
            }),
      );
    }
  }, [classColor, data, iconColor]);

  const columns = [
    {
      title: '회차',
      dataIndex: 'number',
      key: 'number',
      align: 'center',
      width: '15%',
    },
    {
      title: 'Lesson Code',
      dataIndex: 'lesson_code',
      key: 'lesson_code',
      align: 'center',
      width: '25%',
    },
    {
      title: '제목',
      dataIndex: 'title',
      key: 'title',
      align: 'center',
      width: '30%',
      render: (text, record) => {
        return (
          <>
            <Badge
              className="site-badge-count-109"
              count={record?.group?.toUpperCase()}
              style={{ backgroundColor: iconColor[record?.group] }}
            />
            <span>&nbsp;{text}</span>
          </>
        );
      },
    },
    {
      title: '학습날짜',
      dataIndex: 'studyDate',
      key: 'studyDate',
      align: 'center',
      render: (text, record, index) => {
        const names = `studyDate-${record.key}`;
        form.setFieldsValue({
          [names]: text && moment(text, 'YYYY-MM-DD'),
        });
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Form.Item name={`studyDate-${record.key}`}>
              {/* <DatePicker name={`studyDate-${record.key}`} defaultValue={text && moment(text, 'YYYY-MM-DD')} /> */}
              <DatePicker name={`studyDate-${record.key}`} initialValues={text && moment(text, 'YYYY-MM-DD')} />
            </Form.Item>
            {/* {text && record.key.toString().indexOf('add') === -1 && (
              <Button shape="link" size="default" icon={<PlusSquareOutlined />} onClick={() => handleAddrow(index, record)}></Button>
            )} */}
            {/* 날짜 지정 안 했어도 추가 버튼 보이도록 변경 */}
            <Button shape="link" size="default" icon={<PlusSquareOutlined />} onClick={() => handleAddrow(index, record)}></Button>
          </div>
        );
      },
    },
  ];

  function handleAddrow(index, record) {
    if (studyList.map((item) => item.key).indexOf(`add-${record.key}`) !== -1) {
      alert('하나만 추가할 수 있습니다.');
      return false;
    }
    const newStudyList = [...studyList];
    newStudyList.splice(index + 1, 0, {
      ...newStudyList[index],
      key: `add-${record.key}`,
      studyDate: null,
      number: `${newStudyList[index].number} 복사`,
    });
    setStudyList(newStudyList);
  }

  function handleSelect() {
    console.log('handleSelect');
  }

  // type -> all 시에 모든 스케쥴 재조정
  function handleEventChange(changeInfo, type = '') {
    const {
      start,
      extendedProps: { classLessonIdx },
    } = changeInfo.event.toPlainObject();
    setClassLessonIdx(classLessonIdx);
    setStudyList((prev) => {
      return prev.map((study) => {
        if (study.key === classLessonIdx) {
          return {
            ...study,
            studyDate: start ? start : study.studyDate,
          };
        } else {
          return {
            ...study,
          };
        }
      });
    });

    updateClassLession();
  }

  function showUpdateConfirm(changeInfo) {
    handleEventChange(changeInfo);
  }

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((formData) => {
        setStudyList((prev) => {
          return prev.map((study) => {
            const formValue = formData[`studyDate-${study.key}`] && moment(formData[`studyDate-${study.key}`]).format('YYYY-MM-DD');

            return {
              ...study,
              studyDate: formValue ? formValue : null,
              isAdd: study.key.toString().indexOf('add') !== -1,
            };
          });
        });

        update();
      })
      .catch((errorInfo) => {
        console.error('errorInfo', errorInfo);
      });
  };

  useEffect(() => {
    window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 1000);
  });

  return (
    <>
      <Modal
        className="modify-schedule-modal"
        centered
        visible={visible}
        title={
          <>
            일정수정{' '}
            <Typography.Text style={{ fontSize: '1.2em', fontWeight: 'bold', paddingLeft: '20px' }}>{classItems?.name}</Typography.Text>
          </>
        }
        onCancel={() => {
          setReset(reset + 1); //체크박스 초기화 하려고 추가함.
          handleCancel();
        }}
        width={'80%'}
        footer={[
          <Button key="submit" type="primary" onClick={handleFormSubmit} loading={loadingUpdateClassLesson}>
            수정
          </Button>,
        ]}
        //footer={false}
      >
        <Row gutter={[16, 16]}>
          <Col span={12} className="modify-schedule-modal-a">
            <Row>
              <Col offset={15}>
                <Text></Text>
                {classItems?.begin_date && classItems?.end_date
                  ? `${moment(classItems?.begin_date).format('YYYY-MM-DD')} ~ ${
                      classItems?.end_date && moment(classItems?.end_date).format('YYYY-MM-DD')
                    }`
                  : ''}
              </Col>
            </Row>
            <Form form={form} component={false} onFinish={handleFormSubmit}>
              <CustomTable
                className="detaildatapopup-table"
                loading={studentLoading}
                dataSource={studyList}
                columns={columns}
                color="#edf3fb"
                scroll={{ y: 500 }}
                pagination={{
                  total: studyList.length,
                  pageSize: studyList.length,
                  hideOnSinglePage: true,
                }}
                bordered
              />
            </Form>
          </Col>
          <Col span={12} className="modify-schedule-modal-b">
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              selectable={true}
              events={events}
              eventOrder={'key'}
              droppable={true}
              editable={true}
              eventContent={(eventInfo, i) => {
                return (
                  <>
                    <div
                      style={{
                        backgroundColor: eventInfo.backgroundColor,
                        color: 'white',
                        borderRadius: 10,
                        width: 20,
                        textAlign: 'center',
                        marginRight: 5,
                        fontSize: '0.5rem',
                      }}
                    >
                      {eventInfo.event.title}
                    </div>
                    <div>{classType[eventInfo.textColor]}</div>
                  </>
                );
              }}
              select={handleSelect}
              eventChange={showUpdateConfirm}
              locale={'ko-KR'}
            />
            {isMiddle && (
              <>
                <div justifyContent={'flex-end'}>
                  <Select
                    // mode="multiple"
                    showSearch
                    name="book_code"
                    style={{ width: '60%' }}
                    placeholder="레벨을 선택하세요"
                    value={bookCodes}
                    onChange={setBookCodes}
                    filterOption={(input, option) => {
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                  >
                    {bookList.map((book) => (
                      <Option key={`title_${book.key}`} value={book.key}>
                        {book.value}
                      </Option>
                    ))}
                  </Select>
                  {/* <br/> */}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex' }}>
                      <Button
                        htmlType="submit"
                        style={{
                          background: '#646A6D',
                          borderRadius: 6,
                          margin: 5,
                          color: 'white',
                        }}
                        size={'large'}
                        loading={addBookLoading}
                        onClick={handelSaveAddBooks}
                      >
                        Add Book
                      </Button>
                      <Button
                        style={{
                          background: deleteActive ? '#f52516' : '#646A6D',
                          borderRadius: 6,
                          marginTop: 5,
                          color: 'white',
                        }}
                        size={'large'}
                        onClick={handelDeleteBooks}
                      >
                        Delete Book
                      </Button>
                    </div>
                    <Button
                      style={{
                        borderRadius: 6,
                        margin: 5,
                      }}
                      key="submit"
                      type="primary"
                      onClick={handleFormSubmit}
                      loading={loadingUpdateClassLesson}
                    >
                      수정
                    </Button>
                  </div>
                </div>
                <CustomTable
                  rowKey={(record) => record.idx}
                  className="detaildatapopup-table"
                  dataSource={classBooks}
                  columns={columnBooks}
                  color="#edf3fb"
                  scroll={{ y: 'calc(100vh - 254px)' }}
                  bordered
                />
              </>
            )}
          </Col>
        </Row>
      </Modal>
    </>
  );
}

export default ClassScheduleModal;
const columnBooks = [
  {
    title: 'Code',
    dataIndex: 'book_code',
    key: 'book_code',
    align: 'center',
    render: (text, record) => {
      return <span>{record?.book?.book_sub[0]?.title || text}</span>;
    },
  },
];
